import { Box, Button, Container, Flex, Heading, Stack } from '@chakra-ui/react'
import { faEdit, faUser } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'

import { TIME_WITH_TIMEZONE_SHORT_FORMAT } from '@/constants/date'

import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionHostQ from '@/queries/SessionHostQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import { useClientNow } from '@/client/hooks'
import { type Query, SQL } from '@/client/query'
import { getAdminUrl, getSettingsUrl, getUserUrl } from '@/client/services/url'
import { getActiveAnniversary, hasUserRole } from '@/client/services/user'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'
import isUserDefined from '@/client/services/user/isUserDefined'

import TutorFollowButton from '@/components/Buttons/TutorFollowButton'
import UserSendMessageButton from '@/components/Buttons/UserSendMessageButton'
import ProfileMoreOptionsMenu from '@/components/Profile/ProfileMoreOptionsMenu'
import ShwLink from '@/components/ShwLink'
import { TutorProfileTag } from '@/components/Tutor/TutorProfileTag'
import Tippy from '@/components/UI/Tippy'
import UserAvatar from '@/components/UserAvatar'
import AnniversaryIcon from '@/components/Worlds/UI/Anniversary/AnniversaryIcon'

export default function ProfileHeader({ user }: { user: Query }): React.ReactElement {
  const currentUser = useUser()
  const [now] = useClientNow()
  const userRoles = user(UserQ.roles) ?? []
  const isUserTutor = user(UserQ.hasTutorRole()) ?? true
  const displayName = user(UserQ.display_name) ?? ''
  const username = user(UserQ.username)
  const userId = user(UserQ.id)
  const pronouns = user(UserQ.pronouns)
  const timezone = user(UserQ.timezone) ?? ''
  const isCurrentUser = user(UserQ.id) === currentUser.id
  const memberSince = DateTime.fromISO(`${user(UserQ.member_since)}`)
  const activeAnniversary = getActiveAnniversary(memberSince)

  const memberSinceExpandedString = `${memberSince.toLocaleString(
    DateTime.DATE_FULL
  )} at ${memberSince.toLocaleString(TIME_WITH_TIMEZONE_SHORT_FORMAT)}`

  const registeredForSessionWithTutor =
    user<boolean>`EXISTS(SELECT 1 ${SessionAttendeeQ.where({
      userId: currentUser.id,
      include: [
        {
          association: 'Session',
          required: true,
          include: [
            {
              association: 'Hosts',
              required: true,
              where: SQL`${SessionHostQ.user_id} = ${UserQ.id}`,
            },
          ],
        },
      ],
    })})` ?? false

  const sendMessageButtonDisabledMessage =
    !hasUserRole(currentUser, 'admin:super') &&
    !hasUserRole(currentUser, 'user:tutor') &&
    !registeredForSessionWithTutor
      ? `You can message this tutor after you've joined a session with them!`
      : null

  return (
    <Container>
      <Stack
        width="100%"
        direction={['column', 'column', 'row']}
        align="center"
        spacing={['24px', '40px']}
      >
        <UserAvatar
          src={`${user(UserQ.avatar_url)}`}
          name={`${displayName}`}
          size="2xl"
          progressionLevel={user(UserQ.progression_level)}
        />
        <Stack
          direction={['column', 'column', 'row']}
          align="center"
          justify="space-between"
          width="100%"
          spacing="32px"
        >
          <Stack direction="column" spacing="0px" align={['center', 'center', 'flex-start']}>
            <Stack
              direction="row"
              align="center"
              spacing={0}
              flexWrap="wrap"
              justifyContent="middle"
              mb="4px"
            >
              <Box flexGrow={1}>
                <Heading
                  size="3xl"
                  as="h1"
                  fontSize={['32px', '48px']}
                  whiteSpace="nowrap"
                  width="100%"
                  textAlign={['center', 'left']}
                  pr={6}
                  pl={[6, 0]}
                >
                  {displayName}
                </Heading>
              </Box>
              <TutorProfileTag tutor={user} />
            </Stack>
            {username == null ? null : (
              <ShwLink
                href={getUserUrl({ userId: userId ?? 0, username })}
                variant="subtle-gray"
                fontSize="24px"
              >
                @{username}
              </ShwLink>
            )}
            <Box
              whiteSpace="pre-wrap"
              color="fg.muted"
              textAlign={['center', 'center', 'left']}
              pt="16px"
            >
              {activeAnniversary != null ? (
                <>
                  <AnniversaryIcon
                    years={activeAnniversary}
                    includeTooltip
                    fontSize="18px"
                    isCurrentUser={isCurrentUser}
                    displayName={displayName}
                  />
                  &nbsp;&nbsp;
                </>
              ) : null}
              <Tippy content={memberSinceExpandedString} disabled={!isCurrentUser}>
                <span>Joined {memberSince.toFormat('LLL y')}</span>
              </Tippy>
              {pronouns == null ? '' : ` · ${pronouns}`}
              {' · '}
              {now.setZone(timezone).toLocaleString(DateTime.TIME_SIMPLE)} Local
            </Box>
          </Stack>
          <Flex flex={1} justify="flex-end" width={['100%', 'auto']}>
            <Stack direction={['column', 'row']} width={['100%', 'auto']} spacing="8px">
              {hasUserAuthScope(currentUser, 'admin:users:read') ? (
                <ShwLink href={getAdminUrl(`user/${userId}`)} width={['100%', 160]}>
                  <Button
                    width={['100%', 160]}
                    variant="danger"
                    leftIcon={
                      <Box minW={6} textAlign="center">
                        <FontAwesomeIcon size="sm" icon={faUser} />
                      </Box>
                    }
                  >
                    Admin Page
                  </Button>
                </ShwLink>
              ) : null}
              {isCurrentUser ? (
                <ShwLink href={getSettingsUrl()} width={['100%', 160]}>
                  <Button
                    width={['100%', 160]}
                    leftIcon={
                      <Box minW={6} textAlign="center">
                        <FontAwesomeIcon size="sm" icon={faEdit} />
                      </Box>
                    }
                  >
                    Edit Profile
                  </Button>
                </ShwLink>
              ) : (
                <>
                  {isUserTutor ? <TutorFollowButton width={['100%', 120]} tutor={user} /> : null}
                  <UserSendMessageButton
                    userId={userId}
                    userRoles={userRoles}
                    disabledMessage={sendMessageButtonDisabledMessage}
                  />
                  {isUserDefined(currentUser) && userId != null ? (
                    <ProfileMoreOptionsMenu userId={userId} displayName={displayName} />
                  ) : null}
                </>
              )}
            </Stack>
          </Flex>
        </Stack>
      </Stack>
    </Container>
  )
}
